import React from 'react';
import { Box, Stack, Typography } from '@mui/material';
import { finiteNumber, formatNumber } from './taskMapData';
const ZONE_LABELS = { low: 'Низкий NDVI', medium: 'Средний NDVI', high: 'Высокий NDVI' };
const validNdvi = (value) => finiteNumber(value) && value >= -1 && value <= 1;
// Saved observations are authoritative; never infer old ranges from rates or current imagery.
export function canShowFabbriNdviZones(prescription) {
    if ((prescription === null || prescription === void 0 ? void 0 : prescription.method) !== 'fabbri_ndvil' || !Array.isArray(prescription.zones))
        return false;
    const rates = new Set();
    const names = new Set();
    let active = 0;
    for (const zone of prescription.zones) {
        if (!zone || !finiteNumber(zone.pixel_count) || zone.pixel_count < 0)
            return false;
        if (zone.pixel_count === 0)
            continue;
        const rate = Math.fround(zone.rate);
        if (!['low', 'medium', 'high'].includes(zone.zone) || names.has(zone.zone)
            || !finiteNumber(zone.rate) || zone.rate <= 0 || !finiteNumber(rate) || rate <= 0 || rates.has(rate)
            || !validNdvi(zone.ndvi_min) || !validNdvi(zone.ndvi_max) || zone.ndvi_min > zone.ndvi_max)
            return false;
        rates.add(rate);
        names.add(zone.zone);
        active++;
    }
    return active > 0;
}
function savedNdviPrecision(prescription) {
    var _a, _b;
    const endpoints = [];
    for (const zone of prescription.zones) {
        if (zone.pixel_count !== 0 && validNdvi(zone.ndvi_min) && validNdvi(zone.ndvi_max) && zone.ndvi_min <= zone.ndvi_max) {
            endpoints.push(zone.ndvi_min, zone.ndvi_max);
        }
    }
    const q33 = (_a = prescription.thresholds) === null || _a === void 0 ? void 0 : _a.q33;
    const q66 = (_b = prescription.thresholds) === null || _b === void 0 ? void 0 : _b.q66;
    if (prescription.zoning_mode !== 'uniform' && validNdvi(q33) && validNdvi(q66) && q33 <= q66)
        endpoints.push(q33, q66);
    const unique = [...new Set(endpoints)];
    const losesDetail = (format) => new Set(unique.map(format)).size !== unique.length
        || unique.some((value) => value !== 0 && Number(format(value)) === 0);
    let fractionDigits = 3;
    while (fractionDigits < 9 && losesDetail((value) => value.toFixed(fractionDigits)))
        fractionDigits++;
    if (!losesDetail((value) => value.toFixed(fractionDigits)))
        return { fractionDigits };
    let significantDigits = 9;
    while (significantDigits < 17 && losesDetail((value) => value.toPrecision(significantDigits)))
        significantDigits++;
    return { fractionDigits, significantDigits };
}
function ndviLabels(minimum, maximum, precision) {
    if (precision) {
        const format = (value) => precision.significantDigits
            ? value.toPrecision(precision.significantDigits) : value.toFixed(precision.fractionDigits);
        return [format(minimum), format(maximum)];
    }
    // Three decimals normally suffice. Preserve small real differences instead of displaying a fake equality.
    let digits = 3;
    const losesDetail = () => (minimum !== maximum && minimum.toFixed(digits) === maximum.toFixed(digits))
        || (minimum !== 0 && Number(minimum.toFixed(digits)) === 0)
        || (maximum !== 0 && Number(maximum.toFixed(digits)) === 0);
    while (losesDetail() && digits < 7)
        digits++;
    if (losesDetail()) {
        return [minimum.toPrecision(9), maximum.toPrecision(9)];
    }
    return [minimum.toFixed(digits), maximum.toFixed(digits)];
}
export function fabbriNdviRangeLabel(zone, precision) {
    if ((zone === null || zone === void 0 ? void 0 : zone.pixel_count) === 0)
        return 'NDVI: нет пикселей';
    const minimum = zone === null || zone === void 0 ? void 0 : zone.ndvi_min;
    const maximum = zone === null || zone === void 0 ? void 0 : zone.ndvi_max;
    if (!validNdvi(minimum) || !validNdvi(maximum) || minimum > maximum) {
        return 'Диапазон NDVI недоступен';
    }
    const [lower, upper] = ndviLabels(minimum, maximum, precision);
    return minimum === maximum ? `NDVI = ${lower}` : `NDVI: ${lower} — ${upper}`;
}
export function fabbriNdviBoundaryCaption(prescription, precision) {
    var _a, _b;
    if (prescription.zoning_mode === 'uniform')
        return null;
    const q33 = (_a = prescription.thresholds) === null || _a === void 0 ? void 0 : _a.q33;
    const q66 = (_b = prescription.thresholds) === null || _b === void 0 ? void 0 : _b.q66;
    if (!validNdvi(q33) || !validNdvi(q66) || q33 > q66)
        return 'Границы деления NDVI в сохранённом расчёте недоступны.';
    const [lower, upper] = ndviLabels(q33, q66, precision);
    return q33 === q66
        ? `Границы сохранённого расчёта: низкий NDVI < ${lower}; средний NDVI = ${lower}; высокий NDVI > ${lower}.`
        : `Границы сохранённого расчёта: низкий NDVI ≤ ${lower}; средний ${lower} < NDVI < ${upper}; высокий NDVI ≥ ${upper}.`;
}
// Raster values are float32: match their real rates, never legacy five-kg display buckets.
export function fabbriNitrogenLegendRows(prescription, items, area) {
    const uniform = prescription.zoning_mode === 'uniform';
    const zoneNames = uniform ? ['medium'] : ['low', 'medium', 'high'];
    return zoneNames.map((name) => {
        var _a;
        const zone = prescription.zones.find((row) => row.zone === name);
        const candidates = zone && finiteNumber(zone.rate) ? items.filter((item) => finiteNumber(item.value)) : [];
        const exact = candidates.find((item) => item.value === zone.rate);
        const nearest = candidates.reduce((closest, item) => !closest
            || Math.abs(item.value - zone.rate) < Math.abs(closest.value - zone.rate) ? item : closest, null);
        const match = exact !== null && exact !== void 0 ? exact : (nearest && Math.abs(nearest.value - zone.rate) <= Math.abs(zone.rate) * 2e-6 ? nearest : null);
        const repeatedRate = zone && prescription.zones.filter((row) => row.rate === zone.rate).length > 1;
        // Subnormal float32 rates can coincide: their merged chart class cannot be attributed to every zone.
        const hectares = (zone === null || zone === void 0 ? void 0 : zone.pixel_count) === 0 ? 0 : repeatedRate && finiteNumber(zone === null || zone === void 0 ? void 0 : zone.area_ha) ? zone.area_ha
            : match && finiteNumber(match.ha) ? match.ha : null;
        return { name, label: uniform ? 'Единая базовая норма' : ZONE_LABELS[name], zone, color: (_a = match === null || match === void 0 ? void 0 : match.color) !== null && _a !== void 0 ? _a : null, hectares,
            share: finiteNumber(hectares) && finiteNumber(area) && area > 0 ? hectares / area * 100 : null };
    });
}
export default function FabbriNitrogenLegend({ prescription, items, area, showNdviZones = false }) {
    const uniform = prescription.zoning_mode === 'uniform';
    const rows = fabbriNitrogenLegendRows(prescription, items, area);
    // One precision across all cards and quantiles prevents neighbouring saved endpoints
    // from looking equal just because each individual wide range rounded to three decimals.
    const ndviPrecision = savedNdviPrecision(prescription);
    const boundaries = fabbriNdviBoundaryCaption(prescription, ndviPrecision);
    return React.createElement(Box, { component: "section", "aria-label": showNdviZones ? 'Легенда исходных зон NDVI сохранённого расчёта'
            : uniform ? 'Легенда единой нормы азота' : 'Легенда азота по трём зонам NDVI', sx: { mt: 0.5, minWidth: 0 } },
        React.createElement(Typography, { variant: "body2", sx: { fontWeight: 600, mb: 1 } }, showNdviZones
            ? uniform ? 'Исходный NDVI · единая зона' : 'Исходные зоны NDVI · сохранённый расчёт'
            : uniform ? 'Единая норма по полю' : 'Три зоны NDVI · нормы азота'),
        uniform && React.createElement(Typography, { variant: "body2", color: "text.secondary", sx: { mb: 1 } }, "\u041F\u0438\u043B\u043E\u0442\u043D\u044B\u0439 \u0444\u0438\u043B\u044C\u0442\u0440: \u0440\u0430\u0437\u043B\u0438\u0447\u0438\u044F NDVI \u043C\u0430\u043B\u044B (\u0440\u0430\u0437\u043C\u0430\u0445 < 0,20 \u0438 CV < 5%). \u041D\u0430 \u0432\u0441\u0435\u0439 \u043F\u043B\u043E\u0449\u0430\u0434\u0438 \u0441 \u0434\u0430\u043D\u043D\u044B\u043C\u0438 \u043E\u0441\u0442\u0430\u0432\u043B\u0435\u043D\u0430 \u0431\u0430\u0437\u043E\u0432\u0430\u044F \u043D\u043E\u0440\u043C\u0430."),
        React.createElement(Box, { component: "ul", sx: { display: 'grid', gridTemplateColumns: { xs: 'minmax(0, 1fr)', md: uniform ? 'minmax(0, 1fr)' : 'repeat(3, minmax(0, 1fr))' },
                gap: 1, p: 0, m: 0, listStyle: 'none' } }, rows.map((row) => {
            var _a, _b, _c, _d, _e;
            return React.createElement(Box, { component: "li", key: row.name, sx: { border: '1px solid', borderColor: 'divider', borderRadius: 1, p: 1.25, minWidth: 0 } },
                React.createElement(Stack, { direction: "row", spacing: 1, alignItems: "center", sx: { mb: 0.75 } },
                    React.createElement(Box, { "aria-hidden": "true", style: { backgroundColor: (_a = row.color) !== null && _a !== void 0 ? _a : 'transparent' }, sx: { width: 22, height: 22, flexShrink: 0,
                            borderRadius: 0.75, border: '1px solid rgba(0,0,0,0.2)' } }),
                    React.createElement(Typography, { variant: "body2", fontWeight: 600 }, showNdviZones && uniform ? 'Вся площадь с данными' : row.label)),
                showNdviZones ? React.createElement(React.Fragment, null,
                    React.createElement(Typography, { variant: "body1", fontWeight: 600 }, fabbriNdviRangeLabel(row.zone, ndviPrecision)),
                    React.createElement(Typography, { variant: "body2", color: "text.secondary" },
                        "\u041D\u043E\u0440\u043C\u0430: ",
                        formatNumber((_b = row.zone) === null || _b === void 0 ? void 0 : _b.rate, 2),
                        " \u043A\u0433 N/\u0433\u0430")) : React.createElement(React.Fragment, null,
                    React.createElement(Typography, { variant: "body1", fontWeight: 600 },
                        formatNumber((_c = row.zone) === null || _c === void 0 ? void 0 : _c.rate, 2),
                        " \u043A\u0433 N/\u0433\u0430"),
                    React.createElement(Typography, { variant: "body2", color: "text.secondary" }, fabbriNdviRangeLabel(row.zone, ndviPrecision))),
                React.createElement(Typography, { variant: "caption", color: "text.secondary", sx: { display: 'block' } },
                    "\u0411\u0430\u0437\u043E\u0432\u0430\u044F \u043D\u043E\u0440\u043C\u0430 \u00D7 ",
                    formatNumber((_d = row.zone) === null || _d === void 0 ? void 0 : _d.coefficient, 1).replace('.', ',')),
                React.createElement(Typography, { variant: "body2", sx: { mt: 0.5 } },
                    formatNumber(row.hectares, 2),
                    " \u0433\u0430 \u00B7 ",
                    formatNumber(row.share, 1),
                    "%"),
                ((_e = row.zone) === null || _e === void 0 ? void 0 : _e.pixel_count) === 0 && React.createElement(Typography, { variant: "caption", color: "text.secondary" }, "\u041D\u0435\u0442 \u043F\u0438\u043A\u0441\u0435\u043B\u0435\u0439 \u0432 \u044D\u0442\u043E\u0439 \u0437\u043E\u043D\u0435"));
        })),
        React.createElement(Typography, { variant: "caption", color: "text.secondary", sx: { display: 'block', mt: 0.75 } },
            showNdviZones ? 'Те же зоны и площади сохранённой карты азота, а не общая пятиступенчатая карта NDVI. ' : '',
            "\u041F\u043E\u043A\u0430\u0437\u0430\u043D\u044B \u0444\u0430\u043A\u0442\u0438\u0447\u0435\u0441\u043A\u0438\u0435 \u043C\u0438\u043D\u0438\u043C\u0443\u043C\u044B \u0438 \u043C\u0430\u043A\u0441\u0438\u043C\u0443\u043C\u044B NDVI \u0432 \u043A\u0430\u0436\u0434\u043E\u0439 \u0437\u043E\u043D\u0435."),
        boundaries && React.createElement(Typography, { variant: "caption", color: "text.secondary", sx: { display: 'block', mt: 0.5 } }, boundaries),
        React.createElement(Typography, { variant: "caption", color: "text.secondary", sx: { display: 'block', mt: 0.75 } }, uniform ? 'Площадь и доля указаны только для участков с данными. Пропуски не означают нулевую норму.'
            : 'Доли указаны от площади с данными. Средняя норма зависит от площади зон и может отличаться от базовой. Пропуски не означают нулевую норму.'));
}
